import { RequirementWorkspaceTestCase } from '../types/requirements';

export type TestCaseExportFormat = 'ado' | 'jira';

export interface TestCaseCsvExport {
  headers: string[];
  rows: string[][];
  csv: string;
  filename: string;
}

interface BuildTestCaseCsvExportOptions {
  format: TestCaseExportFormat;
  projectName?: string | null;
  areaPath?: string | null;
  iterationPath?: string | null;
  timestamp?: Date;
}

interface NormalizedStep {
  label: string;
  action: string;
  expected: string;
}

const ADO_HEADERS = [
  'Work Item Type',
  'Title',
  'Test Step',
  'Step Action',
  'Step Expected',
  'Area Path',
  'Iteration Path',
  'Assigned To',
  'State',
];

const JIRA_HEADERS = [
  'Summary',
  'Issue Type',
  'Description',
  'Priority',
  'Labels',
];

function cleanText(value: unknown): string {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => cleanText(item)).filter(Boolean).join(' | ');
  }

  if (typeof value === 'object') {
    const entries = Object.values(value as Record<string, unknown>)
      .map((item) => cleanText(item))
      .filter(Boolean);
    return entries.join(' | ');
  }

  return String(value).trim();
}

function dedupe(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  return values
    .map((value) => cleanText(value))
    .filter((value) => {
      if (!value) {
        return false;
      }

      const key = value.toLowerCase();
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
}

function slugifyLabel(value: string): string {
  const normalized = cleanText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');

  return normalized.replace(/^-+|-+$/g, '');
}

function sanitizeFilenameSegment(value: string): string {
  const normalized = cleanText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');

  return normalized.replace(/^-+|-+$/g, '') || 'test-cases';
}

function normalizePriority(value: RequirementWorkspaceTestCase['priority']): string {
  const normalized = cleanText(value);
  if (!normalized) {
    return 'Medium';
  }

  switch (normalized.toLowerCase()) {
    case '1':
    case 'highest':
    case 'critical':
      return 'Highest';
    case '2':
    case 'high':
      return 'High';
    case '3':
    case 'medium':
      return 'Medium';
    case '4':
    case 'low':
      return 'Low';
    case '5':
    case 'lowest':
      return 'Lowest';
    default:
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }
}

function normalizeIssueType(testCase: RequirementWorkspaceTestCase): string {
  const rawType = cleanText(testCase.type);
  if (!rawType || rawType.toLowerCase() === 'test case') {
    return 'Test';
  }

  return rawType;
}

function normalizeWorkItemType(testCase: RequirementWorkspaceTestCase): string {
  return cleanText(testCase.type) || 'Test Case';
}

function normalizeState(testCase: RequirementWorkspaceTestCase): string {
  return cleanText(testCase.state) || 'Design';
}

function normalizeAssignedTo(testCase: RequirementWorkspaceTestCase): string {
  return cleanText(testCase.assigned_to ?? testCase.assignedTo);
}

function buildAreaPath(testCase: RequirementWorkspaceTestCase, projectName?: string | null): string {
  const segments = dedupe([projectName || undefined, ...(testCase.folder_path || [])]);
  return segments.join('\\');
}

function buildLabels(testCase: RequirementWorkspaceTestCase, format: TestCaseExportFormat): string {
  const rawLabels = dedupe([
    ...(testCase.tags || []),
    ...(testCase.labels || []),
  ]);
  const fallbackLabels = dedupe([
    testCase.category,
    testCase.sub_category,
    testCase.test_type,
    testCase.execution_target,
  ]);
  const labels = rawLabels.length > 0 ? rawLabels : fallbackLabels;

  if (format === 'jira') {
    return labels.map((label) => slugifyLabel(label)).filter(Boolean).join(',');
  }

  return labels.join('; ');
}

function normalizeSteps(testCase: RequirementWorkspaceTestCase): NormalizedStep[] {
  const rawSteps = Array.isArray(testCase.steps) ? testCase.steps : [];
  const normalizedSteps = rawSteps
    .map((rawStep, index) => {
      if (typeof rawStep === 'string') {
        return {
          label: String(index + 1),
          action: cleanText(rawStep),
          expected: '',
        };
      }

      const step = (rawStep || {}) as Record<string, unknown>;
      return {
        label: cleanText(step.step_id ?? step.id ?? index + 1) || String(index + 1),
        action: cleanText(step.action ?? step.step ?? step.description ?? step.text ?? step.instruction),
        expected: cleanText(
          step.expected_result
          ?? step.expectedResult
          ?? step.expected
          ?? step.result
          ?? step.outcome,
        ),
      };
    })
    .filter((step) => step.action || step.expected);

  if (normalizedSteps.length > 0) {
    return normalizedSteps;
  }

  return [
    {
      label: '1',
      action: '',
      expected: cleanText(testCase.expected),
    },
  ];
}

function buildJiraDescription(testCase: RequirementWorkspaceTestCase, steps: NormalizedStep[]): string {
  const description = cleanText(testCase.description);
  const sections = [description];

  if (!description) {
    sections.push(`Generated test case for ${cleanText(testCase.title) || cleanText(testCase.test_key)}.`);
  }

  if (cleanText(testCase.linked_user_story?.title)) {
    sections.push(`Linked user story: ${cleanText(testCase.linked_user_story?.title)}`);
  }

  if (cleanText(testCase.linked_epic?.title)) {
    sections.push(`Linked epic: ${cleanText(testCase.linked_epic?.title)}`);
  }

  if ((testCase.folder_path || []).length > 0) {
    sections.push(`Folder: ${(testCase.folder_path || []).join(' / ')}`);
  }

  const formattedSteps = [
    '||Test Step||Expected Result||',
    ...steps.map((step, index) => {
      const stepNumber = cleanText(step.label) || String(index + 1);
      const action = step.action ? `${stepNumber}. ${step.action}` : stepNumber;
      const expected = step.expected || '';

      return `|${action.replace(/\|/g, '\\|')}|${expected.replace(/\|/g, '\\|')}|`;
    }),
  ].join('\n');

  if (formattedSteps) {
    sections.push(formattedSteps);
  }

  return sections.filter(Boolean).join('\n\n');
}

function escapeCsvCell(value: string): string {
  const normalized = cleanText(value).replace(/\r?\n/g, '\n');
  if (!/[",\n]/.test(normalized)) {
    return normalized;
  }

  return `"${normalized.replace(/"/g, '""')}"`;
}

function serializeCsv(headers: string[], rows: string[][]): string {
  const headerLine = headers.map((header) => escapeCsvCell(header)).join(',');
  const bodyLines = rows.map((row) => row.map((cell) => escapeCsvCell(cell)).join(','));
  return [headerLine, ...bodyLines].join('\r\n');
}

export function buildTestCaseCsvExport(
  testCases: RequirementWorkspaceTestCase[],
  options: BuildTestCaseCsvExportOptions,
): TestCaseCsvExport {
  const { format, projectName, areaPath, iterationPath, timestamp = new Date() } = options;
  const headers = format === 'ado' ? ADO_HEADERS : JIRA_HEADERS;
  const rows = testCases.flatMap((testCase) => {
    const steps = normalizeSteps(testCase);
    const title = cleanText(testCase.title) || cleanText(testCase.test_key);

    if (format === 'ado') {
      const baseRow = [
        normalizeWorkItemType(testCase),
        title,
        '',
        '',
        '',
        cleanText(areaPath) || buildAreaPath(testCase, projectName),
        cleanText(iterationPath),
        normalizeAssignedTo(testCase),
        normalizeState(testCase),
      ];

      const stepRows = steps.map((step) => [
        '',
        '',
        step.label,
        step.action,
        step.expected,
        '',
        '',
        '',
        '',
      ]);

      return [baseRow, ...stepRows];
    }

    return [[
      title,
      normalizeIssueType(testCase),
      buildJiraDescription(testCase, steps),
      normalizePriority(testCase.priority),
      buildLabels(testCase, 'jira'),
    ]];
  });

  const dateStamp = [
    timestamp.getFullYear(),
    String(timestamp.getMonth() + 1).padStart(2, '0'),
    String(timestamp.getDate()).padStart(2, '0'),
  ].join('-');
  const filename = `${sanitizeFilenameSegment(projectName || 'test-cases')}-test-cases-${format}-${dateStamp}.csv`;
  const csv = serializeCsv(headers, rows);

  return {
    headers,
    rows,
    csv,
    filename,
  };
}